
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LeadFormData } from "@/types/leadForm";
import { hubspotService } from "@/services/hubspotService";
import { 
  SEGMENTO_OPTIONS, 
  CARGO_OPTIONS, 
  FUNCIONARIOS_OPTIONS, 
  AREA_ATUACAO_OPTIONS 
} from "@/constants/leadFormOptions";

interface LeadCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function LeadCaptureModal({ open, onOpenChange, onSuccess }: LeadCaptureModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<LeadFormData>({
    firstname: '',
    email: '',
    phone: '',
    website: '',
    como_voce_ouviu_falar_sobre_a_conversion_: '',
    a_empresa_em_que_trabalho_e___: '',
    seu_cargo: '',
    numero_de_funcionarios_na_empresa_em_que_trabalho: '',
    area_de_atuacao: '',
  });

  const handleInputChange = (field: keyof LeadFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isStep1Valid = () => {
    return formData.firstname.trim() !== '' &&
           formData.email.trim() !== '' &&
           formData.phone.trim() !== '' &&
           formData.website.trim() !== '';
  };

  const isStep2Valid = () => {
    return formData.a_empresa_em_que_trabalho_e___ !== '' &&
           formData.seu_cargo !== '' &&
           formData.numero_de_funcionarios_na_empresa_em_que_trabalho !== '' &&
           formData.area_de_atuacao !== '';
  };

  const handleContinue = () => {
    if (!isStep1Valid()) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!isStep2Valid()) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    setLoading(true);
    console.log("=== INICIANDO PROCESSO DE CADASTRO ===");
    console.log("Dados do formulário:", formData);
    
    try {
      const success = await hubspotService.processLead(formData);
      
      if (success) {
        console.log("=== CADASTRO CONCLUÍDO COM SUCESSO ===");
        toast.success("Cadastro realizado com sucesso!");
        onOpenChange(false);
        onSuccess();
      } else {
        console.log("=== ERRO NO CADASTRO, MAS CONTINUANDO FLUXO ===");
        toast.success("Dados registrados! Iniciando análise...");
        onOpenChange(false);
        onSuccess();
      }
    } catch (error) {
      console.error('=== ERRO FATAL NO CADASTRO ===', error);
      toast.success("Dados registrados! Iniciando análise...");
      onOpenChange(false);
      onSuccess();
    } finally {
      setLoading(false);
      console.log("=== PROCESSO DE CADASTRO FINALIZADO ===");
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      firstname: '',
      email: '',
      phone: '',
      website: '',
      como_voce_ouviu_falar_sobre_a_conversion_: '',
      a_empresa_em_que_trabalho_e___: '',
      seu_cargo: '',
      numero_de_funcionarios_na_empresa_em_que_trabalho: '',
      area_de_atuacao: '',
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            {currentStep === 1 ? "Precisamos de alguns dados seus" : "Só faltam estes dados"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {currentStep === 1 ? (
            <>
              <div className="space-y-2">
                <label htmlFor="firstname" className="text-sm font-medium text-gray-700">
                  Nome completo *
                </label>
                <Input
                  id="firstname"
                  placeholder="Seu nome completo"
                  value={formData.firstname}
                  onChange={(e) => handleInputChange('firstname', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  E-mail corporativo *
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="exemplo@empresa.com.br"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Número de telefone *
                </label>
                <Input
                  id="phone"
                  placeholder="(XX) XXXXX-XXXX"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="website" className="text-sm font-medium text-gray-700">
                  URL do site *
                </label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://www.exemplo.com.br"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="referral" className="text-sm font-medium text-gray-700">
                  Como você ouviu falar sobre a Conversion?
                </label>
                <Input
                  id="referral"
                  placeholder="Google, indicação, redes sociais, etc."
                  value={formData.como_voce_ouviu_falar_sobre_a_conversion_}
                  onChange={(e) => handleInputChange('como_voce_ouviu_falar_sobre_a_conversion_', e.target.value)}
                />
              </div>

              <Button 
                onClick={handleContinue}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!isStep1Valid()}
              >
                Continuar
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label htmlFor="segmento" className="text-sm font-medium text-gray-700">
                  Segmento *
                </label>
                <Select 
                  value={formData.a_empresa_em_que_trabalho_e___} 
                  onValueChange={(value) => handleInputChange('a_empresa_em_que_trabalho_e___', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o segmento" />
                  </SelectTrigger>
                  <SelectContent>
                    {SEGMENTO_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="cargo" className="text-sm font-medium text-gray-700">
                  Seu cargo *
                </label>
                <Select 
                  value={formData.seu_cargo} 
                  onValueChange={(value) => handleInputChange('seu_cargo', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione seu cargo" />
                  </SelectTrigger>
                  <SelectContent>
                    {CARGO_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="funcionarios" className="text-sm font-medium text-gray-700">
                  Número de funcionários *
                </label>
                <Select 
                  value={formData.numero_de_funcionarios_na_empresa_em_que_trabalho} 
                  onValueChange={(value) => handleInputChange('numero_de_funcionarios_na_empresa_em_que_trabalho', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o número de funcionários" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNCIONARIOS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="area" className="text-sm font-medium text-gray-700">
                  Área de atuação *
                </label>
                <Select 
                  value={formData.area_de_atuacao} 
                  onValueChange={(value) => handleInputChange('area_de_atuacao', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a área de atuação" />
                  </SelectTrigger>
                  <SelectContent>
                    {AREA_ATUACAO_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={handleBack}
                  className="flex-1"
                >
                  Voltar
                </Button>
                <Button 
                  onClick={handleSubmit}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={!isStep2Valid() || loading}
                >
                  {loading ? "Cadastrando..." : "Cadastre-se"}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
